import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Shield, Calendar, Activity, Search, UserPlus } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { roleService } from '../../../services/roleService';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import AddMemberModal from './AddMemberModal';

export default function MemberManagementSection({ onStatsUpdate }) {
  const { profile } = useAuth();
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editingMember, setEditingMember] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  useEffect(() => {
    loadMembers();
    loadRoles();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      
      // Guard: Don't query if tenant_id is not available
      if (!profile?.tenant_id) {
        console.warn('Cannot load members: tenant_id is not available');
        setMembers([]);
        onStatsUpdate?.({ totalMembers: 0 });
        return;
      }

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select(`
          id,
          email,
          first_name,
          last_name,
          avatar_url,
          job_title,
          is_active,
          last_login_at,
          created_at,
          user_role_assignments!user_role_assignments_user_id_fkey!inner(
            id,
            role_id,
            assigned_at,
            system_roles(
              id,
              name,
              display_name,
              hierarchy_level
            )
          )
        `)
        ?.eq('tenant_id', profile?.tenant_id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      const transformedMembers = data?.map(member => ({
        id: member?.id,
        email: member?.email,
        firstName: member?.first_name,
        lastName: member?.last_name,
        fullName: `${member?.first_name || ''} ${member?.last_name || ''}`?.trim() || 'N/A',
        avatarUrl: member?.avatar_url,
        jobTitle: member?.job_title,
        isActive: member?.is_active,
        lastLoginAt: member?.last_login_at,
        joinedAt: member?.created_at,
        roles: member?.user_role_assignments?.map(ra => ({
          assignmentId: ra?.id,
          roleId: ra?.role_id,
          roleName: ra?.system_roles?.name,
          displayName: ra?.system_roles?.display_name,
          hierarchyLevel: ra?.system_roles?.hierarchy_level,
          assignedAt: ra?.assigned_at
        })) || []
      })) || [];

      setMembers(transformedMembers);
      onStatsUpdate?.({ totalMembers: transformedMembers?.length });
    } catch (error) {
      console.error('Error loading members:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const allRoles = await roleService?.getAllRoles();
      setRoles(allRoles || []);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const handleRoleChange = async (memberId, newRoleId) => {
    try {
      // Remove existing role assignments for this user in this tenant
      const member = members?.find(m => m?.id === memberId);
      if (member?.roles?.length > 0) {
        const { error: deleteError } = await supabase
          ?.from('user_role_assignments')
          ?.delete()
          ?.eq('user_id', memberId)
          ?.eq('tenant_id', profile?.tenant_id);

        if (deleteError) throw deleteError;
      }

      // Assign new role
      const { error: assignError } = await supabase
        ?.from('user_role_assignments')
        ?.insert({
          user_id: memberId,
          role_id: newRoleId,
          tenant_id: profile?.tenant_id,
          assigned_by: profile?.id
        });

      if (assignError) throw assignError;

      await loadMembers();
      setEditingMember(null);
    } catch (error) {
      console.error('Error changing role:', error);
      alert('Failed to update member role: ' + error?.message);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this member from the organization?')) {
      return;
    }

    try {
      // Remove role assignments
      const { error: roleError } = await supabase
        ?.from('user_role_assignments')
        ?.delete()
        ?.eq('user_id', memberId)
        ?.eq('tenant_id', profile?.tenant_id);

      if (roleError) throw roleError;

      // Remove organization membership
      const { error: membershipError } = await supabase
        ?.from('user_organization_memberships')
        ?.delete()
        ?.eq('user_id', memberId)
        ?.eq('tenant_id', profile?.tenant_id);

      if (membershipError) throw membershipError;

      await loadMembers();
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member: ' + error?.message);
    }
  };

  const handleToggleStatus = async (memberId, currentStatus) => {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ is_active: !currentStatus })
        ?.eq('id', memberId);

      if (error) throw error;

      await loadMembers();
    } catch (error) {
      console.error('Error toggling member status:', error);
      alert('Failed to update member status: ' + error?.message);
    }
  };

  const filteredMembers = members?.filter(member => {
    const matchesSearch = 
      member?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || 
      member?.roles?.some(r => r?.roleId === roleFilter);

    return matchesSearch && matchesRole;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Member Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            View, edit, and manage organization members and their roles
          </p>
        </div>
        <Button
          onClick={() => setShowAddMemberModal(true)}
          className="flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          <span>Add Member</span>
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search members by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e?.target?.value)}
          >
            <option value="all">All Roles</option>
            {roles?.map(role => (
              <option key={role?.id} value={role?.id}>
                {role?.displayName}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="ml-2">Loading members...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredMembers?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No members found
                  </td>
                </tr>
              ) : (
                filteredMembers?.map((member) => (
                  <tr key={member?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {member?.avatarUrl ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={member?.avatarUrl}
                              alt={`${member?.fullName} avatar`}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-medium">
                                {member?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{member?.fullName}</div>
                          <div className="text-sm text-gray-500">{member?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingMember === member?.id ? (
                        <Select
                          value={member?.roles?.[0]?.roleId || ''}
                          onChange={(e) => handleRoleChange(member?.id, e?.target?.value)}
                          className="text-sm"
                        >
                          {roles?.map(role => (
                            <option key={role?.id} value={role?.id}>
                              {role?.displayName}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {member?.roles?.[0]?.displayName || 'No Role'}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        {formatDate(member?.joinedAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Activity className="w-4 h-4" />
                        {formatDate(member?.lastLoginAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(member?.id, member?.isActive)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member?.isActive
                            ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {member?.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {editingMember === member?.id ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingMember(null)}
                          >
                            Cancel
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingMember(member?.id)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemoveMember(member?.id)}
                          className="text-error hover:bg-error/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        tenantId={profile?.tenant_id}
        tenantName={profile?.tenant_name}
        onMemberAdded={loadMembers}
      />
    </div>
  );
}